import React from 'react';
import * as Sentry from '@sentry/react';
import { ErrorPage } from '../../pages/Error/ErrorPage';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import i18next from 'i18next';

class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    const errorId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      level: this.props.level || 'error',
      tags: {
        errorBoundary: true,
      },
    });

    this.setState({
      errorInfo,
      errorId,
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo, errorId);
    }
  }


  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          errorId: this.state.errorId,
          resetError: this.handleReset,
        });
      }

      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorId={this.state.errorId}
          onReset={this.handleReset}
          showDetails={this.props.showDetails}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorBoundaryFallback = ({
  error, errorInfo, errorId, onReset, showDetails,
}) => {
  const location = useLocation();
  const prevPathnameRef = React.useRef(location.pathname);

  React.useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      prevPathnameRef.current = location.pathname;
      onReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const detectLanguage = () => {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const pathLang = pathParts[0];
    
    if (pathLang && ['en', 'ar'].includes(pathLang)) {
      return pathLang;
    }
    
    return localStorage.getItem('preferred_language') || 'ar';
  };

  const [currentLang, setCurrentLang] = React.useState(() => {
    const lang = detectLanguage();
    if (i18next.language !== lang) {
      i18next.changeLanguage(lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }
    return lang;
  });

  React.useEffect(() => {
    const lang = detectLanguage();
    if (lang !== currentLang) {
      setCurrentLang(lang);
      i18next.changeLanguage(lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }
  });

  const { t } = useTranslation('application');



  return (
    <ErrorPage
      title={t('errorBoundary.title')}
      subtitle={t('errorBoundary.subtitle')}
      buttonText={t('errorBoundary.retry')}
      buttonAction={onReset}
    />
  );
};

export const ErrorBoundary = ErrorBoundaryClass;

export { ErrorBoundaryClass };
